/**
 * Wallet-free sign-in for the play/UGC deployment.
 *
 * Only rendered when `VITE_DEV_LOGIN=true`, which is never set on the
 * production build — this hits `/api/auth/dev-login`, a route the API does
 * not even register unless `ALLOW_DEV_LOGIN` and `NODE_ENV=development` are
 * both true server-side, so this control is inert against production even if
 * it somehow shipped there by mistake.
 *
 * A label rather than a raw address: filming needs repeatable, memorable
 * personas ("alice", "bored-shopper") across takes, not a fresh random
 * account every click. The label is hashed into a stable fake address, so
 * the same label always signs back into the same account.
 */
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { meKey } from '../hooks/useAuth'
import { api } from '../lib/api'

export const devLoginAvailable = import.meta.env.VITE_DEV_LOGIN === 'true'

/** Deterministic, not cryptographic — just enough spread to avoid collisions
 * between short labels typed by hand. */
function addressFromLabel(label: string): string {
  let h1 = 0xdeadbeef
  let h2 = 0x41c6ce57
  for (let i = 0; i < label.length; i++) {
    const c = label.charCodeAt(i)
    h1 = Math.imul(h1 ^ c, 2654435761)
    h2 = Math.imul(h2 ^ c, 1597334677)
  }
  const hex = (n: number) => (n >>> 0).toString(16).padStart(8, '0')
  // 40 hex chars needed (0x + 20 bytes); five 8-char blocks from two running
  // hashes mixed a few different ways gives that with only two accumulators.
  return `0x${(
    hex(h1) +
    hex(h2) +
    hex(h1 ^ h2) +
    hex(h1 + h2) +
    hex(h2 ^ 0x9e3779b9)
  ).slice(0, 40)}`
}

export function DevLogin() {
  const queryClient = useQueryClient()
  const [label, setLabel] = useState('')
  const [pending, setPending] = useState(false)

  const signInAs = async (address?: string) => {
    setPending(true)
    try {
      await api.post('/auth/dev-login', address ? { address } : undefined)
      await queryClient.invalidateQueries({ queryKey: meKey })
    } finally {
      setPending(false)
    }
  }

  return (
    <form
      className="flex items-center gap-1.5"
      onSubmit={(e) => {
        e.preventDefault()
        if (label.trim()) void signInAs(addressFromLabel(label.trim().toLowerCase()))
      }}
    >
      <input
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        placeholder="persona"
        className="panel-2 w-24 px-2 py-1.5 text-xs outline-none"
        disabled={pending}
      />
      <button type="submit" className="btn btn-ghost btn-sm" disabled={pending || !label.trim()}>
        Dev sign in
      </button>
      <button
        type="button"
        className="btn btn-quiet btn-sm"
        disabled={pending}
        onClick={() => void signInAs()}
        title="Sign in as the default dev account"
      >
        Default
      </button>
    </form>
  )
}
